import React, { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useProjects } from '../context/ProjectContext';
import { createProject, ProjectSummary } from '../services/api';
import ProjectSettingsModal from './ProjectSettingsModal';

const DEFAULT_VISIBLE = 5;

interface Props {
  onProjectSelected?: (projectId: string | null) => void;
}

const FolderIcon: React.FC<{ color: string }> = ({ color }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
);

const ProjectSidebar: React.FC<Props> = ({ onProjectSelected }) => {
  const { colors } = useTheme();
  const { projects, activeProjectId, selectProject, refreshProjects } = useProjects();
  const [expanded, setExpanded] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [settingsProjectId, setSettingsProjectId] = useState<string | null>(null);

  const visible = expanded ? projects : projects.slice(0, DEFAULT_VISIBLE);
  const hasMore = projects.length > DEFAULT_VISIBLE;

  const handleCreate = async () => {
    if (isCreating) return;
    const name = window.prompt('New project name');
    if (!name || !name.trim()) return;
    try {
      setIsCreating(true);
      const project = await createProject({ name: name.trim() });
      await refreshProjects();
      await selectProject(project.id);
      onProjectSelected?.(project.id);
      setSettingsProjectId(project.id);
    } catch (err) {
      console.error('Failed to create project', err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleSelect = async (p: ProjectSummary) => {
    const nextId = activeProjectId === p.id ? null : p.id;
    await selectProject(nextId);
    onProjectSelected?.(nextId);
  };

  const rowStyle = (selected: boolean): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 20px',
    cursor: 'pointer',
    color: colors.textPrimary,
    backgroundColor: selected ? colors.sidebarHover : 'transparent',
    borderLeft: selected ? `3px solid ${colors.primary}` : '3px solid transparent',
    fontSize: '14px',
    userSelect: 'none',
  });

  return (
    <>
      <div style={{ padding: '8px 0', borderBottom: `1px solid ${colors.borderLight}` }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '4px 20px',
            color: colors.textMuted,
            fontSize: '12px',
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
          }}
        >
          <span>Projects</span>
        </div>

        <div onClick={handleCreate} style={rowStyle(false)}>
          <span style={{ display: 'inline-flex', width: 16 }}>+</span>
          <span>New project</span>
        </div>

        {visible.map((p) => (
          <div
            key={p.id}
            style={rowStyle(activeProjectId === p.id)}
            onClick={() => handleSelect(p)}
            onContextMenu={(e) => {
              e.preventDefault();
              setSettingsProjectId(p.id);
            }}
            title="Click to open; right-click to edit"
          >
            <FolderIcon color={p.color || colors.textMuted} />
            <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {p.name}
            </span>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setSettingsProjectId(p.id);
              }}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.textMuted,
                cursor: 'pointer',
                padding: '2px 4px',
                fontSize: '14px',
              }}
              aria-label="Project settings"
            >
              ⋯
            </button>
          </div>
        ))}

        {hasMore && (
          <div
            style={rowStyle(false)}
            onClick={() => setExpanded((v) => !v)}
          >
            <span style={{ display: 'inline-flex', width: 16 }}>⋯</span>
            <span>{expanded ? 'Less' : 'More'}</span>
          </div>
        )}

        {activeProjectId && (
          <div
            style={{
              padding: '6px 20px 10px',
              fontSize: '11px',
              color: colors.textMuted,
            }}
          >
            Filtered to active project. <button
              onClick={() => selectProject(null)}
              style={{
                background: 'transparent',
                border: 'none',
                color: colors.primary,
                cursor: 'pointer',
                padding: 0,
                fontSize: '11px',
              }}
            >Clear</button>
          </div>
        )}
      </div>

      {settingsProjectId && (
        <ProjectSettingsModal
          projectId={settingsProjectId}
          onClose={() => setSettingsProjectId(null)}
        />
      )}
    </>
  );
};

export default ProjectSidebar;
